import { useState } from 'react';
import { useNavigate } from 'react-router';
import { BASE } from '../api/client';
import { useAssetLibrary, useCreateSceneSeries, useSeedFromVideos } from '../hooks/useApi';
import { SceneReferences } from '../components/SceneReferences';

/**
 * Arranque de una serie de escenas: dos puertas de entrada, un solo wizard.
 *
 *  - **Nueva**: se escriben las tomas y cada una lleva SU referencia. Nunca una
 *    bolsa común de imágenes: si el modelo tiene que adivinar qué foto va con
 *    qué toma, se arruina el resultado.
 *  - **Corregir**: se eligen videos ya hechos. El keyframe de cada toma sale del
 *    primer cuadro de su propio video (gratis, ya está pagado), así que cada
 *    toma nace con su imagen correcta y no hay nada que adivinar.
 *
 * Las dos terminan en el mismo lugar: una serie con N tomas, cada una con su
 * imagen. Por eso no son dos wizards distintos.
 */

type Mode = 'nueva' | 'corregir';

interface DraftScene {
  title: string;
  description: string;
  durationSec: number;
  referenceAssetIds: string[];
}

export function SceneSeriesNewPage() {
  const [mode, setMode] = useState<Mode>('nueva');
  const [title, setTitle] = useState('');

  return (
    <div className="flex flex-col gap-5 p-6">
      <h1 className="text-lg font-semibold text-neutral-100">Nueva serie de escenas</h1>

      <div className="flex gap-2">
        <ModeButton
          active={mode === 'nueva'}
          onClick={() => setMode('nueva')}
          title="Arrancar de cero"
          hint="Escribís las tomas y cada una lleva su referencia."
        />
        <ModeButton
          active={mode === 'corregir'}
          onClick={() => setMode('corregir')}
          title="Corregir videos que ya tengo"
          hint="Entran como tomas, con su imagen sacada del propio video."
        />
      </div>

      <label className="flex flex-col gap-1 text-sm text-neutral-300">
        Nombre de la serie
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Sala de reuniones — Grupo Proa"
          className="max-w-md rounded bg-neutral-800 px-2 py-1.5 text-neutral-100"
        />
      </label>

      <p className="max-w-2xl text-xs text-neutral-500">
        Una serie es <strong className="text-neutral-400">un ambiente</strong>. Si las tomas son de lugares
        distintos o de clientes distintos, conviene hacer series separadas: lo que se aprovecha acá es que
        compartan lugar, estilo y ritmo.
      </p>

      {mode === 'nueva' ? <NuevaForm title={title} /> : <CorregirForm title={title} />}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  title,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  hint: string;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'flex max-w-xs flex-col gap-1 rounded-lg border p-3 text-left transition',
        active
          ? 'border-emerald-600 bg-emerald-950/30'
          : 'border-neutral-800 bg-neutral-900 hover:border-neutral-700',
      ].join(' ')}
    >
      <span className="text-sm font-medium text-neutral-100">{title}</span>
      <span className="text-xs text-neutral-500">{hint}</span>
    </button>
  );
}

// ── Modo 1: serie nueva, referencia por toma ─────────────────────────────────

function NuevaForm({ title }: { title: string }) {
  const navigate = useNavigate();
  const create = useCreateSceneSeries();
  const [scenes, setScenes] = useState<DraftScene[]>([
    { title: 'Toma 1', description: '', durationSec: 5, referenceAssetIds: [] },
  ]);

  const patch = (i: number, d: Partial<DraftScene>) =>
    setScenes((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...d } : s)));

  const submit = () => {
    create.mutate(
      { title: title || 'Serie sin nombre', scenes },
      { onSuccess: (res) => navigate(`/scene-series/${res.id}`) },
    );
  };

  return (
    <section className="flex flex-col gap-3">
      {scenes.map((s, i) => (
        <article key={i} className="flex flex-col gap-2 rounded-lg bg-neutral-900 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={s.title}
              onChange={(e) => patch(i, { title: e.target.value })}
              className="w-40 rounded bg-neutral-800 px-2 py-1 text-sm text-neutral-100"
            />
            <input
              value={s.description}
              onChange={(e) => patch(i, { description: e.target.value })}
              placeholder="Qué se ve en esta toma"
              className="min-w-64 flex-1 rounded bg-neutral-800 px-2 py-1 text-sm text-neutral-100"
            />
            <input
              type="number"
              min={2}
              max={30}
              value={s.durationSec}
              onChange={(e) => patch(i, { durationSec: Number(e.target.value) })}
              className="w-16 rounded bg-neutral-800 px-2 py-1 text-sm text-neutral-100"
            />
            <span className="text-xs text-neutral-500">s</span>
            {scenes.length > 1 && (
              <button
                onClick={() => setScenes((prev) => prev.filter((_, idx) => idx !== i))}
                className="text-neutral-500 hover:text-red-400"
              >
                ✕
              </button>
            )}
          </div>

          <SceneReferences
            value={s.referenceAssetIds}
            onChange={(ids) => patch(i, { referenceAssetIds: ids })}
          />
        </article>
      ))}

      <div className="flex items-center gap-3">
        <button
          onClick={() =>
            setScenes((prev) => [
              ...prev,
              { title: `Toma ${prev.length + 1}`, description: '', durationSec: 5, referenceAssetIds: [] },
            ])
          }
          className="rounded bg-neutral-800 px-3 py-1.5 text-sm text-neutral-200 hover:bg-neutral-700"
        >
          + Agregar toma
        </button>
        <button
          onClick={submit}
          disabled={create.isPending || !scenes.length}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {create.isPending ? 'Creando…' : 'Crear la serie'}
        </button>
        <span className="text-xs text-neutral-500">Crear la serie no genera nada todavía: no cuesta.</span>
      </div>
    </section>
  );
}

// ── Modo 2: corregir videos ya hechos ────────────────────────────────────────

function CorregirForm({ title }: { title: string }) {
  const navigate = useNavigate();
  const create = useCreateSceneSeries();
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const seed = useSeedFromVideos(seriesId);
  const [picked, setPicked] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const library = useAssetLibrary({ q: search });
  const videos = (library.data ?? []).filter((a) => a.mimeType.startsWith('video/')).slice(0, 40);

  const submit = () => {
    create.mutate(
      { title: title || 'Corrección de videos', scenes: [{ title: 'tmp', description: '', durationSec: 5 }] },
      {
        onSuccess: (res) => {
          setSeriesId(res.id);
          // La serie nace con una toma de descarte; los videos elegidos la reemplazan.
          seed.mutate(picked, { onSuccess: () => navigate(`/scene-series/${res.id}`) });
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-3">
      <p className="max-w-2xl text-sm text-neutral-400">
        Elegí los videos que quedaron mal. Cada uno entra como una toma, con su imagen sacada del primer
        cuadro de ese mismo video — no cuesta nada, ya está pagado. Después corregís solo las tomas que
        están mal: cambiás la imagen por centavos y recién cuando te convence rehacés ese plano.
      </p>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar en la biblioteca…"
        className="max-w-md rounded bg-neutral-800 px-2 py-1.5 text-sm text-neutral-100"
      />

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
        {videos.map((a) => {
          const on = picked.includes(a.id);
          const order = picked.indexOf(a.id) + 1;
          return (
            <button
              key={a.id}
              onClick={() => setPicked((p) => (on ? p.filter((x) => x !== a.id) : [...p, a.id]))}
              className={[
                'relative overflow-hidden rounded-lg border text-left',
                on ? 'border-emerald-500' : 'border-neutral-800 hover:border-neutral-600',
              ].join(' ')}
            >
              <img
                src={`${BASE}/assets/${a.id}/thumb`}
                alt={a.id}
                className="aspect-video w-full object-cover"
              />
              {on && (
                <span className="absolute left-2 top-2 rounded-full bg-emerald-600 px-2 py-0.5 text-xs text-white">
                  {order}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {videos.length === 0 && (
        <p className="text-sm text-neutral-600">No hay videos en la biblioteca con ese filtro.</p>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={submit}
          disabled={!picked.length || create.isPending || seed.isPending}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {create.isPending || seed.isPending
            ? 'Preparando…'
            : `Corregir ${picked.length} ${picked.length === 1 ? 'video' : 'videos'}`}
        </button>
        <span className="text-xs text-neutral-500">
          El orden de selección es el orden de las tomas.
        </span>
      </div>
    </section>
  );
}
